<script lang="ts" setup generic="T">
  import type {
    Cell,
    Column,
    ColumnDef,
    Header,
    Row,
    Table as TanstackTable,
  } from '@tanstack/vue-table';
  import { FlexRender } from '@tanstack/vue-table';
  import { computed, watch } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import { indexOf } from '@stuut/common/src/utils';
  import { Skeleton } from '@ui/components/skeleton';
  import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
  } from '@ui/components/table/index';
  import { cn } from '@ui/utils/utils';

  import type { ModelTableEmits } from '.';
  import { getColumnSize } from './table';
  import TableEmpty from './TableEmpty.vue';

  interface ModelTableProps {
    bodyFontSize?: string;
    columns: ColumnDef<T>[];
    headerFontSize?: string;
    hideHeader?: boolean;
    keepSelection?: boolean;
    layout?: 'fixed' | 'auto';
    loading?: boolean;
    noSearchResult?: boolean;
    rowClass?: (row: Row<T>) => HTMLAttributes['class'];
    scrollable?: boolean;
    scrollableHorz?: boolean;
    scrollableVert?: boolean;
    spacedRows?: boolean;
    table: TanstackTable<T>;
    tableClass?: HTMLAttributes['class'];
  }

  const props = withDefaults(defineProps<ModelTableProps>(), {
    bodyFontSize: 'text-xs',
    headerFontSize: 'text-xs',
    layout: 'fixed',
    scrollable: true,
  });

  const emits = defineEmits<ModelTableEmits<T>>();
  const rows = defineModel<T[]>('rows');

  watch(
    () => [props.table.getState().pagination, props.table.getFilteredRowModel().rows],
    () => {
      if (!props.keepSelection && Object.keys(props.table.getState().rowSelection).length) {
        props.table.resetRowSelection(true);
      }
    },
    { deep: true },
  );

  const onRowDeleted = (row: Row<T>): void => {
    if (rows.value) {
      const idx = indexOf(props.table.getRowModel().rows, (r: Row<T>) => r.id === row.id);
      const rowsCopy = rows.value.slice();
      rowsCopy.splice(idx, 1);
      rows.value = rowsCopy;
    }
    emits('rowDeleted', row);
  };

  const getUserSelection = () => window.getSelection()?.toString();

  const lastLeftPinnedId = computed(() => props.table.getLeftLeafColumns().at(-1)?.id);
  const firstRightPinnedId = computed(() => props.table.getRightLeafColumns()[0]?.id);

  const getStickyStyle = (column: Column<T, unknown>) => {
    const pinned = column.getIsPinned();
    if (!pinned) return {};
    return pinned === 'left'
      ? { position: 'sticky' as const, left: `${column.getStart('left')}px` }
      : { position: 'sticky' as const, right: `${column.getAfter('right')}px` };
  };

  const getHeaderCellClass = (header: Header<T, unknown>, index: number) => {
    const meta = header.column.columnDef.meta as { class?: string } | undefined;
    const pinned = header.column.getIsPinned();
    return cn('p-2 bg-table-header border-b-2 border-table-border', meta?.class ?? '', {
      'z-[1]': !!pinned,
      'border-r-2': pinned === 'left' && header.column.id === lastLeftPinnedId.value,
      'border-l-2': pinned === 'right' && header.column.id === firstRightPinnedId.value,
      'pl-4': index === 0,
    });
  };

  const getBodyCellClass = (cell: Cell<T, unknown>, index: number) => {
    const meta = cell.column.columnDef.meta as { class?: string } | undefined;
    const pinned = cell.column.getIsPinned();
    return cn('p-2 truncate border-b border-table-border', meta?.class ?? '', {
      'bg-background': !!pinned,
      'z-[1]': !!pinned,
      'border-r-2': pinned === 'left' && cell.column.id === lastLeftPinnedId.value,
      'border-l-2': pinned === 'right' && cell.column.id === firstRightPinnedId.value,
      'pl-4': index === 0,
    });
  };
</script>

<template>
  <div
    :class="
      cn(
        'border',
        scrollable && 'overflow-auto overscroll-none flex-1',
        scrollableHorz && 'overflow-y-hidden overflow-x-auto flex-1',
        scrollableVert && 'overflow-x-hidden overflow-y-auto flex-1',
      )
    "
  >
    <Table
      :class="
        cn(
          'model-table',
          layout === 'fixed' ? 'table-fixed' : 'table-auto',
          spacedRows && 'border-separate spaced-rows border-spacing-y-2.5',
        )
      "
      :table-class="tableClass"
      :spaced-rows="spacedRows"
      :border-rounded="false"
    >
      <TableHeader v-if="!hideHeader" class="sticky z-30 top-0 bg-table-header">
        <TableRow v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
          <TableHead
            v-for="(header, index) in headerGroup.headers"
            :key="header.id"
            :class="
              cn(
                'group relative text-muted-foreground font-medium',
                headerFontSize,
                getHeaderCellClass(header, index),
              )
            "
            :style="[getColumnSize(header.column, layout), getStickyStyle(header.column)]"
          >
            <FlexRender
              v-if="!header.isPlaceholder"
              :props="header.getContext()"
              :render="header.column.columnDef.header"
            />
            <div
              v-if="header.column.getCanResize()"
              class="absolute right-0 top-0 h-full w-2 cursor-col-resize select-none group-hover:border-r-2 group-hover:border-dashed hover:border-foreground"
              :class="{ 'border-r-2 border-dashed': header.column.getIsResizing() }"
              @mousedown="header.getResizeHandler()?.($event)"
              @dblclick="header.column.resetSize()"
            />
          </TableHead>
        </TableRow>
      </TableHeader>

      <TableBody :col-span="columns.length">
        <template v-if="loading">
          <TableRow
            v-for="i in 15"
            :key="i"
            class="relative group text-nowrap"
            :class="[bodyFontSize]"
          >
            <TableCell
              v-for="(col, colIndex) in table.getVisibleLeafColumns()"
              :key="col.id"
              :class="cn(getBodyCellClass({ column: col } as any, colIndex), 'py-3')"
              :style="[getColumnSize(col, layout), getStickyStyle(col)]"
            >
              <Skeleton class="h-4 w-full" />
            </TableCell>
          </TableRow>
        </template>

        <template v-else-if="table.getRowModel().rows?.length">
          <TableRow
            v-for="row in table.getRowModel().rows"
            :key="row.id"
            :data-state="row.getIsSelected() && 'selected'"
            :data-row-id="row.id"
            :data-row-deleted="(row.original as { isDeleted?: boolean })?.isDeleted"
            class="relative group text-nowrap"
            :class="[bodyFontSize]"
          >
            <TableCell
              v-for="(cell, cellIndex) in row.getVisibleCells()"
              :key="cell.id"
              :class="getBodyCellClass(cell, cellIndex)"
              :style="[getColumnSize(cell.column, layout), getStickyStyle(cell.column)]"
              @click="
                (e: MouseEvent) => {
                  if (
                    cell.column.id !== 'select' &&
                    !getUserSelection() &&
                    !(cell.column.columnDef.meta as any)?.disableClick
                  ) {
                    emits('cellClick', cell, e);
                    emits('rowClick', row, e);
                  }
                }
              "
            >
              <FlexRender
                :props="cell.getContext()"
                :render="cell.column.columnDef.cell"
                @deleted="onRowDeleted(row)"
                @updated="(d: T) => emits('rowUpdated', d)"
                @created="emits('rowCreated', row)"
              />
            </TableCell>
          </TableRow>
        </template>

        <TableEmpty v-else-if="noSearchResult" :colspan="columns.length" class="h-[200px]">
          <slot name="empty">
            <div class="flex flex-col w-full text-center mx-auto gap-y-1 py-1">
              <h2 class="text-foreground font-medium">No Results</h2>
              <span class="text-muted-foreground text-xs">Please try a different search.</span>
            </div>
          </slot>
        </TableEmpty>
      </TableBody>
    </Table>
  </div>
</template>

<style lang="postcss">
  @reference "../../globals.css";

  .model-table {
    @apply text-xs!;

    /* border-collapse: separate so sticky cell borders stay with their cells,
       not shared/collapsed with adjacent non-sticky cells */
    border-collapse: separate;

    &:not(.spaced-rows) {
      border-spacing: 0;
    }

    thead tr {
      @apply h-9;
    }

    tbody tr {
      @apply h-12;
    }

    th button[role='checkbox'],
    td:first-child button[role='checkbox'] {
      @apply shrink-0;
    }

    tbody {
      /* Flat rows — override Table.vue's alternating row colors */
      tr:not([data-slot='table-empty']) {
        background-color: transparent !important;

        &:hover td {
          background-color: color-mix(
            in oklch,
            var(--table-odd-hover) 50%,
            var(--background)
          ) !important;
        }

        &[data-state='selected'] td {
          background-color: var(--table-selected) !important;
        }
      }

      td {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    &.spaced-rows {
      tr {
        td:nth-child(1),
        th:nth-child(1) {
          @apply rounded-l-sm;
        }

        td:nth-last-child(1),
        th:nth-last-child(1) {
          @apply rounded-r-sm;
        }
      }
    }
  }
</style>
